/**
 * Conflict Detection Utility
 *
 * Detects scheduling conflicts (teacher, room, class double-booking)
 * using time-overlap comparison. After the redesign, different classes
 * use different class_schedules so period_id values are not comparable
 * across schedules — conflicts are detected by (day, time_start, time_end) overlap.
 */

import type { TimetableRecordWithRelations } from '@timetable/types';
import type { Conflict } from '../components/timetable';

/**
 * Returns true when two time windows overlap.
 * Uses strict inequality so back-to-back periods (e.g. 09:00–10:00 and 10:00–11:00) do NOT conflict.
 */
function timesOverlap(startA: string, endA: string, startB: string, endB: string): boolean {
  return startA < endB && startB < endA;
}

/** Canonical slot key: min(startA,startB)–max(startA,startB) so (a,b) and (b,a) produce the same key */
function slotKey(id: string, day: string, startA: string, startB: string): string {
  const lo = startA <= startB ? startA : startB;
  const hi = startA <= startB ? startB : startA;
  return `${id}-${day}-${lo}-${hi}`;
}

function formatTeacherConflictMessage(teacherName: string, day: string, periodNumber: number): string {
  return `${teacherName} is already teaching another class on ${day}, Period ${periodNumber} — select a different teacher`;
}

function formatRoomConflictMessage(roomName: string, day: string, periodNumber: number): string {
  return `${roomName} is already booked on ${day}, Period ${periodNumber} — select a different room`;
}

function formatClassConflictMessage(sectionName: string, day: string, periodNumber: number): string {
  return `${sectionName} already has multiple subjects scheduled on ${day}, Period ${periodNumber}`;
}

/**
 * Detect teacher conflicts (teacher assigned to multiple overlapping time windows on the same day)
 */
export function detectTeacherConflicts(records: TimetableRecordWithRelations[]): Conflict[] {
  const conflicts: Conflict[] = [];

  const byTeacher = new Map<string, TimetableRecordWithRelations[]>();
  for (const record of records) {
    if (!record.teacher_id) continue;
    if (!byTeacher.has(record.teacher_id)) byTeacher.set(record.teacher_id, []);
    byTeacher.get(record.teacher_id)!.push(record);
  }

  for (const [teacherId, teacherRecords] of byTeacher.entries()) {
    const byDay = new Map<string, TimetableRecordWithRelations[]>();
    for (const r of teacherRecords) {
      if (!byDay.has(r.day_of_week)) byDay.set(r.day_of_week, []);
      byDay.get(r.day_of_week)!.push(r);
    }

    for (const [day, dayRecords] of byDay.entries()) {
      // One conflict per unique overlapping time-window pair — prevents duplicates when
      // a teacher is assigned to N sections at the same slot (C(N,2) pairs → 1 conflict).
      const emitted = new Set<string>();

      for (let i = 0; i < dayRecords.length; i++) {
        for (let j = i + 1; j < dayRecords.length; j++) {
          const a = dayRecords[i];
          const b = dayRecords[j];
          const aStart = a.period?.time_start;
          const aEnd = a.period?.time_end;
          const bStart = b.period?.time_start;
          const bEnd = b.period?.time_end;

          if (!aStart || !aEnd || !bStart || !bEnd) continue;
          if (!timesOverlap(aStart, aEnd, bStart, bEnd)) continue;

          const key = slotKey(teacherId, day, aStart, bStart);
          if (emitted.has(key)) continue;
          emitted.add(key);

          const teacherName = a.teacher?.name || 'Unknown Teacher';
          const periodNumber = a.period?.period_number || 0;

          const hasSupervision = [a, b].some(r => r.is_supervision);
          const hasTeaching = [a, b].some(r => !r.is_supervision);

          let message: string;
          if (hasSupervision && hasTeaching) {
            message = `${teacherName} is scheduled for both teaching and supervision at the same time (${day}, Period ${periodNumber}) — resolve the overlap`;
          } else if (hasSupervision) {
            message = `${teacherName} has overlapping supervision duties on ${day}, Period ${periodNumber}`;
          } else {
            message = formatTeacherConflictMessage(teacherName, day, periodNumber);
          }

          conflicts.push({
            id: `teacher-${key}`,
            type: 'teacher',
            severity: 'hard',
            message,
            affectedRecords: [a, b],
          });
        }
      }
    }
  }

  return conflicts;
}

/**
 * Detect room conflicts (room assigned to multiple overlapping time windows on the same day)
 */
export function detectRoomConflicts(records: TimetableRecordWithRelations[]): Conflict[] {
  const conflicts: Conflict[] = [];

  const byRoom = new Map<string, TimetableRecordWithRelations[]>();
  for (const record of records) {
    if (!record.room_id) continue;
    if (!byRoom.has(record.room_id)) byRoom.set(record.room_id, []);
    byRoom.get(record.room_id)!.push(record);
  }

  for (const [roomId, roomRecords] of byRoom.entries()) {
    const byDay = new Map<string, TimetableRecordWithRelations[]>();
    for (const r of roomRecords) {
      if (!byDay.has(r.day_of_week)) byDay.set(r.day_of_week, []);
      byDay.get(r.day_of_week)!.push(r);
    }

    for (const [day, dayRecords] of byDay.entries()) {
      const emitted = new Set<string>();

      for (let i = 0; i < dayRecords.length; i++) {
        for (let j = i + 1; j < dayRecords.length; j++) {
          const a = dayRecords[i];
          const b = dayRecords[j];
          const aStart = a.period?.time_start;
          const aEnd = a.period?.time_end;
          const bStart = b.period?.time_start;
          const bEnd = b.period?.time_end;

          if (!aStart || !aEnd || !bStart || !bEnd) continue;
          if (!timesOverlap(aStart, aEnd, bStart, bEnd)) continue;

          const key = slotKey(roomId, day, aStart, bStart);
          if (emitted.has(key)) continue;
          emitted.add(key);

          const roomName = (a.room as any)?.room_name || (a.room as any)?.name || 'Unknown Room';
          const periodNumber = a.period?.period_number || 0;

          conflicts.push({
            id: `room-${key}`,
            type: 'room',
            severity: 'hard',
            message: formatRoomConflictMessage(roomName, day, periodNumber),
            affectedRecords: [a, b],
          });
        }
      }
    }
  }

  return conflicts;
}

/**
 * Detect class section conflicts (section has multiple subjects at overlapping times on the same day)
 */
export function detectClassConflicts(records: TimetableRecordWithRelations[]): Conflict[] {
  const conflicts: Conflict[] = [];

  const bySection = new Map<string, TimetableRecordWithRelations[]>();
  for (const record of records) {
    const sectionId = record.class_section_id;
    if (!bySection.has(sectionId)) bySection.set(sectionId, []);
    bySection.get(sectionId)!.push(record);
  }

  for (const [sectionId, sectionRecords] of bySection.entries()) {
    const byDay = new Map<string, TimetableRecordWithRelations[]>();
    for (const r of sectionRecords) {
      if (!byDay.has(r.day_of_week)) byDay.set(r.day_of_week, []);
      byDay.get(r.day_of_week)!.push(r);
    }

    for (const [day, dayRecords] of byDay.entries()) {
      const emitted = new Set<string>();

      for (let i = 0; i < dayRecords.length; i++) {
        for (let j = i + 1; j < dayRecords.length; j++) {
          const a = dayRecords[i];
          const b = dayRecords[j];
          const aStart = a.period?.time_start;
          const aEnd = a.period?.time_end;
          const bStart = b.period?.time_start;
          const bEnd = b.period?.time_end;

          if (!aStart || !aEnd || !bStart || !bEnd) continue;
          if (!timesOverlap(aStart, aEnd, bStart, bEnd)) continue;

          const key = slotKey(sectionId, day, aStart, bStart);
          if (emitted.has(key)) continue;
          emitted.add(key);

          const cs = a.class_section;
          const sectionName = cs ? `${cs.class.class_name}-${cs.section}` : 'Unknown Section';
          const periodNumber = a.period?.period_number || 0;

          conflicts.push({
            id: `class-${key}`,
            type: 'class',
            severity: 'hard',
            message: formatClassConflictMessage(sectionName, day, periodNumber),
            affectedRecords: [a, b],
          });
        }
      }
    }
  }

  return conflicts;
}

/**
 * Detect all conflicts in the timetable
 */
export function detectAllConflicts(records: TimetableRecordWithRelations[]): Conflict[] {
  return [
    ...detectTeacherConflicts(records),
    ...detectRoomConflicts(records),
    ...detectClassConflicts(records),
  ];
}

/**
 * Check if a specific period assignment would create conflicts.
 * The newRecord must have period.time_start and period.time_end populated
 * for time-overlap detection to work correctly.
 */
export function wouldCreateConflict(
  newRecord: Partial<TimetableRecordWithRelations>,
  existingRecords: TimetableRecordWithRelations[]
): { hasConflict: boolean; conflicts: Conflict[] } {
  const tempId = (newRecord as any).id ?? '__new__';
  const tempRecords = [...existingRecords, newRecord as TimetableRecordWithRelations];
  const allConflicts = detectAllConflicts(tempRecords);

  // A conflict is relevant if the temp record is one of the affected records.
  const relevantConflicts = allConflicts.filter(conflict =>
    conflict.affectedRecords.some(r => r.id === tempId)
  );

  return { hasConflict: relevantConflicts.length > 0, conflicts: relevantConflicts };
}

/**
 * Get conflicts for a specific record
 */
export function getConflictsForRecord(recordId: string, allConflicts: Conflict[]): Conflict[] {
  return allConflicts.filter(conflict => conflict.affectedRecords.some(r => r.id === recordId));
}

/**
 * Check if a record has any conflicts
 */
export function hasConflicts(recordId: string, allConflicts: Conflict[]): boolean {
  return getConflictsForRecord(recordId, allConflicts).length > 0;
}
